import { datadogRum } from '@datadog/browser-rum';
import { Box } from '@mui/material';
import { useAuth } from '@teamexos/fit-shared';
import { LoginButton } from 'components/common/Button';
import NewTextField from 'components/common/fields/NewTextField';
import SignUpQrCode from 'components/SignUpQrCode';
import useMixpanel from 'hooks/useMixpanel';
import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router';
import { getAppCheckHeader, useFirebase } from '../../contexts/firebaseContext';
import Branding from './Branding';
import {
  Content,
  ErrorContainer,
  Footer,
  Layout,
  Note,
  SignUp,
  SignUpSubtitle,
  SignUpText,
  SignUpTitle,
  Subtitle,
  TextLink,
  Title,
} from './styles';

interface StartForm {
  email: string;
}

const INVALID_CREDENTIALS_MESSAGE =
  'Invalid credentials. If the issue persists, please contact your benefits provider.';

const REJOIN_GYM_ON_MOBILE_MESSAGE =
  'Your membership is inactive. Open the Exos app on your mobile device to rejoin your gym.';

const Start = () => {
  const {
    control,
    handleSubmit,
    setValue,

    formState: { isValid, errors, isSubmitting },
  } = useForm<StartForm>({
    reValidateMode: 'onChange',
    mode: 'onChange',
  });
  const navigate = useNavigate();
  const location = useLocation();
  const { email } = useParams();
  const { authorize, userCheck, forgotPasswordStart } = useAuth();
  const { appCheck } = useFirebase();
  const [loading, setLoading] = useState(false);

  const track = useMixpanel();
  const mixpanelData = useMemo(
    () => ({
      event_category: 'registration',
      event_location: 'registration_welcome',
    }),
    [],
  );
  const [error, setError] = useState<string | undefined>(undefined);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing code prior to biome migration
  useEffect(() => {
    // Store the current location in local storage so we can redirect back to it after login
    if (!location.pathname.startsWith('/start')) {
      localStorage.setItem('authRedirect', location.pathname + location.search);
    }
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing code prior to biome migration
  useEffect(() => {
    if (email) {
      setValue('email', email);

      handleSubmit(onSubmit)();
    }
  }, [email]);

  const onSubmit = async (data: StartForm) => {
    setLoading(true);

    track('User Action', {
      event_name: 'email_entered',
      event_type: 'screen interaction',
      ...mixpanelData,
    });

    try {
      const appCheckHeaders = await getAppCheckHeader(appCheck);

      const info = await userCheck({
        email: data.email,
        skipPasswordResetEmail: true,
        extraHeaders: appCheckHeaders,
      });

      datadogRum.addAction('userCheck', { info });

      if (!info || !info?.registrationEnabled) {
        track('Screen View', {
          screen_name: 'registration_invalid_email',
        });

        setError(INVALID_CREDENTIALS_MESSAGE);
        return;
      }

      if (info.active === false && info.registered === true) {
        // heart-web has no join-gym flow, so members who must rejoin their
        // gym are pointed at the mobile app instead.
        setError(
          info.mustJoinGym
            ? REJOIN_GYM_ON_MOBILE_MESSAGE
            : 'This account is inactive.',
        );
        return;
      }

      const cognitoConfirm = info?.hasCognitoUser && !info.isEmailVerified;
      const unregistered = !info?.registered;

      if (cognitoConfirm || unregistered) {
        navigate('/complete-on-mobile');
        return;
      }

      if (info.resetRequired) {
        await forgotPasswordStart({
          connection: info.identity.connection,
          email: data.email,
        });

        navigate('password-expired', {
          state: {
            connection: info.identity.connection,
            provider: info.identity.provider,
            client: info.client,
            email: data.email,
          },
        });
        return;
      }

      setTimeout(async () => {
        if (info.isSSO) {
          await authorize({
            email: data.email,
            connection: info.identity.connection,
            provider: info.identity.provider,
          });
        } else {
          navigate('/login', {
            state: {
              provider: info.identity.provider,
              connection: info.identity.connection,
              client: info.client,
              email: data.email,
            },
          });
        }
      }, 10);
    } catch (e) {
      setError(INVALID_CREDENTIALS_MESSAGE);
      datadogRum.addError(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Branding>
      <Layout>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Content>
            <Box>
              <Title role="heading">Welcome to Exos</Title>
              <Subtitle>
                Sign in with your work email or an eligible email associated
                with your Exos account.
              </Subtitle>

              {error && (
                <ErrorContainer data-testid="error-message">
                  {error}
                </ErrorContainer>
              )}

              <Controller
                name="email"
                control={control}
                defaultValue=""
                rules={{
                  required: 'Email is required',
                }}
                render={({ field: { onChange, value } }) => (
                  <NewTextField
                    label="Email address"
                    inputProps={{
                      type: 'email',
                      autoComplete: 'email',
                      inputMode: 'email',
                    }}
                    value={value}
                    onChange={onChange}
                    isInvalid={!!errors.email}
                    helperText={errors.email?.message?.toString()}
                    mixpanelData={mixpanelData}
                  />
                )}
              />
            </Box>

            <LoginButton
              type="submit"
              disabled={!isValid || loading || isSubmitting}
              role="button"
            >
              Next
            </LoginButton>

            <Footer>
              <TextLink
                target="_blank"
                href="https://exosdigital.zendesk.com/hc/en-us/requests/new?ticket_form_id=13836393583764"
                rel="noopener noreferrer"
                role="link"
              >
                Need help?
              </TextLink>
              <Note role="note">
                Note: Exos is available exclusively to employees and eligible
                dependents of partner organizations.
              </Note>
            </Footer>
          </Content>
        </form>

        <SignUp>
          <SignUpQrCode />
          <SignUpText>
            <SignUpTitle>New to Exos? Sign up</SignUpTitle>
            <SignUpSubtitle>
              Scan this code to download the app and register your account.
            </SignUpSubtitle>
          </SignUpText>
        </SignUp>
      </Layout>
    </Branding>
  );
};

export default Start;
